/**
 * Generator definitions. Flat producers, not compounding tiers.
 *
 * Costs and outputs are strings so they can be handed straight to Decimal
 * without ever passing through a lossy JS number literal.
 *
 * Outputs are set from a target payback time - cost divided by output - of
 * about 32 seconds, easing down to about 22 at the top tier so that saving for
 * a higher tier is always worth it.
 *
 * Retuned when the milestone dropped from x8 to x2: that removed a great deal
 * of growth, and without this the first meltdown slipped from 8:45 to 17:04.
 *
 * Payback time is the growth time-constant of the whole game: output compounds
 * with e^(t/payback). The tiers as first drafted had payback doubling every
 * tier (100s, 110s, 150s, 277s, ... 8636s), which stalled progress tier by
 * tier and made every clock above 150% bank literally nothing. See
 * tools/pacing.ts for the measurements behind these values.
 */

export interface GeneratorDef {
  readonly id: string;
  readonly name: string;
  /** Cost of the first unit, as a Decimal string. */
  readonly baseCost: string;
  /** Thoughts per second contributed by one unit, as a Decimal string. */
  readonly baseOutput: string;
  /** What this thing is, in one line. Shown on the row so a tier is not just a number. */
  readonly role: string;
  /**
   * How many units of the tier below each of these needs in order to fire at
   * full rate. Undefined on Neurons, which are the base and feed themselves.
   *
   * The ratios shrink as the ladder climbs, and they have to: the requirement
   * multiplies all the way down, so one Cerebrum at 5:2:2:2:2:2:2 already
   * implies 320 Neurons. Anything steeper puts the top of the tree beyond what
   * the cost curve can ever pay for.
   */
  readonly needs?: number;
  /**
   * Heat this tier costs per unit of output share, 1 at the base falling to
   * 0.25 at the top. The reason to climb: the same sparks, for less heat.
   */
  readonly heatFactor: number;
}

export const GENERATORS: readonly GeneratorDef[] = [
  {
    id: 'neuron',
    heatFactor: 1,
    name: 'Neuron',
    baseCost: '10',
    baseOutput: '0.31',
    role: 'One cell. Fires, recovers, fires again.',
  },
  {
    id: 'dendrite',
    heatFactor: 0.89,
    needs: 5,
    name: 'Dendrite',
    baseCost: '110',
    baseOutput: '3.6',
    role: 'Input branches. More ways for a signal to arrive.',
  },
  {
    id: 'axon',
    heatFactor: 0.79,
    needs: 2,
    name: 'Axon',
    baseCost: '1.2e3',
    baseOutput: '41',
    role: 'Output cable. Carries the signal somewhere that matters.',
  },
  {
    id: 'synapse',
    heatFactor: 0.68,
    needs: 2,
    name: 'Synapse',
    baseCost: '13e3',
    baseOutput: '470',
    role: 'The gap between two cells, and the crossing of it.',
  },
  {
    id: 'ganglion',
    heatFactor: 0.57,
    needs: 2,
    name: 'Ganglion',
    baseCost: '140e3',
    baseOutput: '5.34e3',
    role: 'A knot of cell bodies that decides things locally.',
  },
  {
    id: 'column',
    heatFactor: 0.46,
    needs: 2,
    name: 'Cortical Column',
    baseCost: '1.6e6',
    baseOutput: '64.5e3',
    role: 'Six layers of cortex acting as one unit.',
  },
  {
    id: 'lobe',
    heatFactor: 0.36,
    needs: 2,
    name: 'Lobe',
    baseCost: '22e6',
    baseOutput: '942e3',
    role: 'A quarter of a hemisphere, given a job.',
  },
  {
    id: 'cerebrum',
    heatFactor: 0.25,
    needs: 2,
    name: 'Cerebrum',
    baseCost: '380e6',
    baseOutput: '17.3e6',
    role: 'The whole thinking mass. There is nothing above this.',
  },
];

export const GENERATOR_COUNT = GENERATORS.length;
